/**
 * ApprovalHistoryModal Component
 *
 * Displays the complete history of an approval request in a timeline view:
 * - Visual timeline of all steps
 * - Action details (who, when, what)
 * - Comments and notes
 * - Status transitions
 *
 * @module components/admin/approval-processes/ApprovalHistoryModal
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ArrowLeft,
  User,
  Calendar,
  MessageSquare,
  FileText,
  UserPlus,
  ArrowRight,
  RefreshCw,
  SkipForward
} from 'lucide-react';
import { StandardModal } from '../../standard/StandardModal';
import { StandardButton } from '../../standard/StandardButton';
import { StandardLoadingState } from '../../standard/StandardLoadingState';
import {
  ApprovalRequest,
  ApprovalStepHistory,
  ApprovalStatus,
  ApprovalAction
} from '../../../types/approvalProcesses';
import { approvalProcessStorage } from '../../../services/approvalProcessStorage';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';

interface ApprovalHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  onViewRecord?: (recordId: string, objectType: string) => void;
}

// Status badge configurations
const statusConfig: Record<ApprovalStatus, { icon: React.ElementType; color: string; bgColor: string; label: string }> = {
  pending: {
    icon: Clock,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    label: 'Pending'
  },
  approved: {
    icon: CheckCircle,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    label: 'Approved'
  },
  rejected: {
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    label: 'Rejected'
  },
  recalled: {
    icon: ArrowLeft,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
    label: 'Recalled'
  },
  escalated: {
    icon: AlertTriangle,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    label: 'Escalated'
  }
};

// Step history status configurations
const stepStatusConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  pending: {
    icon: Clock,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30'
  },
  completed: {
    icon: CheckCircle,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30'
  },
  skipped: {
    icon: SkipForward,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700'
  },
  reassigned: {
    icon: UserPlus,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30'
  }
};

// Action labels
const actionLabels: Record<ApprovalAction, { label: string; icon: React.ElementType; color: string }> = {
  approve: {
    label: 'Approved',
    icon: CheckCircle,
    color: 'text-green-600 dark:text-green-400'
  },
  reject: {
    label: 'Rejected',
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400'
  },
  reassign: {
    label: 'Reassigned',
    icon: UserPlus,
    color: 'text-blue-600 dark:text-blue-400'
  },
  add_comment: {
    label: 'Commented',
    icon: MessageSquare,
    color: 'text-purple-600 dark:text-purple-400'
  },
  recall: {
    label: 'Recalled',
    icon: ArrowLeft,
    color: 'text-gray-600 dark:text-gray-400'
  }
};

export const ApprovalHistoryModal: React.FC<ApprovalHistoryModalProps> = ({
  isOpen,
  onClose,
  requestId,
  onViewRecord
}) => {
  const [request, setRequest] = useState<ApprovalRequest | null>(null);
  const [loading, setLoading] = useState(true);

  // Load request data
  useEffect(() => {
    if (isOpen && requestId) {
      loadRequest();
    }
  }, [isOpen, requestId]);

  const loadRequest = () => {
    setLoading(true);
    try {
      if (USE_GOBODO_API) {
        setRequest(null);
      } else {
        const allRequests = approvalProcessStorage.getAllRequests();
        const found = allRequests.find(r => r.id === requestId);
        setRequest(found || null);
      }
    } catch (error) {
      console.error('Failed to load request:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate duration between dates
  const calculateDuration = (startDate: string, endDate?: string) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
    }
  };

  // Get current status config
  const currentStatus = request ? statusConfig[request.status] : null;
  const StatusIcon = currentStatus?.icon || Clock;

  // Sort step history by step number and date
  const sortedHistory = request?.stepHistory
    .slice()
    .sort((a, b) => {
      if (a.stepNumber !== b.stepNumber) {
        return a.stepNumber - b.stepNumber;
      }
      return new Date(a.assignedDate).getTime() - new Date(b.assignedDate).getTime();
    }) || [];

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Approval History"
      size="lg"
    >
      {loading ? (
        <StandardLoadingState message="Loading approval history..." />
      ) : !request ? (
        <div className="text-center py-8">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Request not found</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Request Summary Header */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {request.recordName}
                  </h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${currentStatus?.bgColor} ${currentStatus?.color}`}>
                    <StatusIcon className="w-4 h-4" />
                    {currentStatus?.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {request.objectType}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Submitted by {request.submittedByName}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(request.submittedDate)}
                  </span>
                </div>
                {request.comments && (
                  <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Submission Comments</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{request.comments}</p>
                  </div>
                )}
              </div>
              {onViewRecord && (
                <StandardButton
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewRecord(request.recordId, request.objectType)}
                >
                  View Record
                </StandardButton>
              )}
            </div>

            {/* Progress indicator */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">Progress</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  Step {request.currentStepNumber} of {request.totalSteps}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    request.status === 'approved' ? 'bg-green-500' :
                    request.status === 'rejected' ? 'bg-red-500' :
                    request.status === 'recalled' ? 'bg-gray-500' :
                    'bg-blue-500'
                  }`}
                  style={{
                    width: request.status === 'approved' || request.status === 'rejected'
                      ? '100%'
                      : `${(request.currentStepNumber / request.totalSteps) * 100}%`
                  }}
                />
              </div>
              {request.completedDate && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Completed in {calculateDuration(request.submittedDate, request.completedDate)}
                </p>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
              Approval Timeline
            </h4>

            <div className="space-y-0">
              {/* Initial submission event */}
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <ArrowRight className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-600 my-2" />
                </div>
                <div className="flex-1 pb-6">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 dark:text-white">Submitted for Approval</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(request.submittedDate)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {request.submittedByName} submitted this {request.objectType.toLowerCase()} for approval
                    </p>
                  </div>
                </div>
              </div>

              {/* Step history events */}
              {sortedHistory.map((step, index) => {
                const stepStatus = stepStatusConfig[step.status] || stepStatusConfig.pending;
                const StepIcon = stepStatus.icon;
                const actionInfo = step.action ? actionLabels[step.action] : null;
                const ActionIcon = actionInfo?.icon;

                return (
                  <div key={step.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full ${stepStatus.bgColor} flex items-center justify-center`}>
                        {actionInfo && ActionIcon ? (
                          <ActionIcon className={`w-5 h-5 ${actionInfo.color}`} />
                        ) : (
                          <StepIcon className={`w-5 h-5 ${stepStatus.color}`} />
                        )}
                      </div>
                      {index < sortedHistory.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-600 my-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">
                              Step {step.stepNumber}: {step.stepName}
                            </span>
                            <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${stepStatus.bgColor} ${stepStatus.color}`}>
                              {step.status.charAt(0).toUpperCase() + step.status.slice(1)}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(step.actionDate || step.assignedDate)}
                          </span>
                        </div>

                        <div className="space-y-2 text-sm">
                          <p className="text-gray-600 dark:text-gray-400">
                            <span className="text-gray-500 dark:text-gray-500">Assigned to:</span>{' '}
                            <span className="font-medium text-gray-700 dark:text-gray-300">{step.assignedToName}</span>
                          </p>

                          {step.action && step.actionByName && (
                            <p className="text-gray-600 dark:text-gray-400">
                              <span className="text-gray-500 dark:text-gray-500">Action by:</span>{' '}
                              <span className="font-medium text-gray-700 dark:text-gray-300">{step.actionByName}</span>
                              {' - '}
                              <span className={actionInfo?.color}>{actionInfo?.label}</span>
                            </p>
                          )}

                          {step.dueDate && (
                            <p className="text-gray-600 dark:text-gray-400">
                              <span className="text-gray-500 dark:text-gray-500">Due:</span>{' '}
                              {formatDate(step.dueDate)}
                            </p>
                          )}

                          {step.comments && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <div className="flex items-center gap-2 mb-1">
                                <MessageSquare className="w-4 h-4 text-gray-400" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">Comments</span>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300">{step.comments}</p>
                            </div>
                          )}

                          {step.actionDate && step.status === 'completed' && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Completed in {calculateDuration(step.assignedDate, step.actionDate)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Final outcome (if completed) */}
              {(request.status === 'approved' || request.status === 'rejected' || request.status === 'recalled') && (
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full ${currentStatus?.bgColor} flex items-center justify-center`}>
                      <StatusIcon className={`w-5 h-5 ${currentStatus?.color}`} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className={`border-2 ${
                      request.status === 'approved' ? 'border-green-200 dark:border-green-800' :
                      request.status === 'rejected' ? 'border-red-200 dark:border-red-800' :
                      'border-gray-200 dark:border-gray-700'
                    } rounded-lg p-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-semibold ${currentStatus?.color}`}>
                          {request.status === 'approved' ? 'Final Approval' :
                           request.status === 'rejected' ? 'Rejected' :
                           'Recalled'}
                        </span>
                        {request.completedDate && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(request.completedDate)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {request.status === 'approved' && 'This request has been fully approved and all required actions have been executed.'}
                        {request.status === 'rejected' && 'This request was rejected and the submitter has been notified.'}
                        {request.status === 'recalled' && 'This request was recalled by the submitter.'}
                      </p>
                      {request.finalComments && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                          <p className="text-sm text-gray-700 dark:text-gray-300">{request.finalComments}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardButton variant="secondary" onClick={onClose}>
              Close
            </StandardButton>
          </div>
        </div>
      )}
    </StandardModal>
  );
};
